/**
 * Asking the browser which controls in a form would not be accepted, before anything is sent.
 *
 * Reads the constraint validation every control already has — `required`, `min`, `max`, a
 * number box with letters in it — so a field is covered the moment it declares its limits,
 * with no list of rules kept beside it. The words are `problemOf`'s, in `errors.ts`, which
 * a test can reach without a browser.
 *
 * A form using this sets `noValidate`, or the browser stops the submit first with a bubble of
 * its own, in its own language, over one field at a time.
 */

import type { FieldProblem } from '../ui/fieldProblem';

import { problemOf, type Invalidity } from './errors';

export function checkForm(
  root: HTMLElement | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): FieldProblem | null {
  if (root === null) return null;

  const found: Invalidity[] = [];

  for (const control of root.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
    'input, select, textarea',
  )) {
    if (control.disabled || control.id === '' || control.validity.valid) continue;

    const validity = control.validity;

    found.push({
      field: control.id,
      label: labelOf(root, control.id),
      numeric: control instanceof HTMLInputElement && control.type === 'number',
      valueMissing: validity.valueMissing,
      badInput: validity.badInput,
      rangeUnderflow: validity.rangeUnderflow,
      rangeOverflow: validity.rangeOverflow,
      stepMismatch: validity.stepMismatch,
      min: control instanceof HTMLInputElement ? control.min : '',
      max: control instanceof HTMLInputElement ? control.max : '',
    });
  }

  return problemOf(found, t);
}

/** The words of the field's label, or its aria-label for a control without one. */
function labelOf(root: HTMLElement, id: string): string {
  const label = root.querySelector(`label[for="${CSS.escape(id)}"]`)?.textContent?.trim();

  return label !== undefined && label !== ''
    ? label
    : (document.getElementById(id)?.getAttribute('aria-label') ?? id);
}

/**
 * A refusal from the server, with the field's label put into the banner.
 *
 * The server names a field by its API name; the person knows it by the words over the box.
 * The box itself keeps the bare reason under it, and the banner says "Not saved. Per
 * kilometre: …", so somebody reading only the top of a long form knows where to look. A
 * refusal about no field, or a field not on the page, is returned as it came.
 */
export function named(
  problem: FieldProblem,
  root: HTMLElement | null,
  t: (key: string, options?: Record<string, unknown>) => string,
): FieldProblem {
  if (problem.field === null || problem.fields !== undefined) return problem;

  const scope = root ?? document.body;

  if (scope.querySelector(`#${CSS.escape(problem.field)}`) === null) return problem;

  return {
    field: problem.field,
    message: t('form.oneField', { label: labelOf(scope, problem.field), message: problem.message }),
    fields: [{ field: problem.field, message: problem.message }],
  };
}
